import { useSelector } from 'react-redux';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { getLineItemRecurringFrequencyBySubtotal } from '../../utils/recurring';
import { useSubtotals } from '../../context/SubtotalsProvider';
import FormattedPrice from '../Utilities/FormattedPrice';
import { FUTURE_START_DATE, RELATIVE_START_DATE } from '../../constants/LineItems';
import { getBuyerOverridesIsLoading } from '../../state/BuyerOverrides/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const hasRelativeDelay = initialPayment => initialPayment.type === RELATIVE_START_DATE && !!initialPayment.relativeDelay;

/**
 * Text that shows up below the Pay button when there are future start date line items.
 * Styling is only applied when the text is displayed on its own (i.e. not a part of a message)
 */
const MaybeFutureChargesTotals = ({
  shouldApplyStyling = true
}) => {
  const {
    loading,
    recurringSubtotals,
    currencyCode
  } = useSubtotals();
  const hasMultipleSubscriptions = recurringSubtotals.length > 1;
  const loadingBuyerOverrides = useSelector(getBuyerOverridesIsLoading);
  if (loading || loadingBuyerOverrides) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "maybe-future-charges-total-loading"
    });
  }
  if (!recurringSubtotals.length) {
    return null;
  }
  if (hasMultipleSubscriptions) {
    return null;
  }
  const {
    recurringBillingTerms: {
      initialPayment: {
        type,
        futureStartDate
      },
      initialPayment,
      intervalLength,
      intervalUnit
    },
    subtotal
  } = recurringSubtotals[0];
  if (type !== FUTURE_START_DATE && type !== RELATIVE_START_DATE) {
    return null;
  }
  const recurringBillingFrequency = getLineItemRecurringFrequencyBySubtotal(intervalUnit, intervalLength);
  const recurringBillingTerm = /*#__PURE__*/_jsx(FormattedPrice, {
    currencyCode: currencyCode,
    price: subtotal,
    frequency: recurringBillingFrequency,
    simplify: false,
    currencyAndAmountProps: {
      typography: 'body-125'
    }
  });
  const delayUnit = hasRelativeDelay(initialPayment) && initialPayment.relativeDelay.delayUnit;
  const delayLength = hasRelativeDelay(initialPayment) && initialPayment.relativeDelay.delayLength;
  const FutureChargesTotalText = () => {
    if (type === FUTURE_START_DATE) {
      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "payments.futureChargesTotals.futureChargesTotalsAndDate",
        options: {
          recurringBillingTerm,
          date: /*#__PURE__*/_jsx(FormattedDateTime, {
            value: Number(futureStartDate),
            format: "LL",
            type: "utc"
          })
        }
      });
    }
    if (type === RELATIVE_START_DATE) {
      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: `payments.futureChargesTotals.futureChargesTotalsAndRelativeDate.${delayUnit}`,
        options: {
          recurringBillingTerm,
          delayLength,
          count: delayLength
        }
      });
    }
    return null;
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: shouldApplyStyling ? /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      children: /*#__PURE__*/_jsx(Text, {
        textAlign: "center",
        as: "small",
        "data-test-id": "maybe-future-charges-totals",
        children: /*#__PURE__*/_jsx(FutureChargesTotalText, {})
      })
    }) : /*#__PURE__*/_jsx(Text, {
      as: "span",
      children: /*#__PURE__*/_jsx(FutureChargesTotalText, {})
    })
  });
};
export default MaybeFutureChargesTotals;